import { ELECTION_NO_RESULT_COLOR } from "@/src/elections/business-layer/constants"
import { Feature, FeatureCollection, Point } from "geojson"
import {
  Layer,
  LeafletMouseEvent,
  PointExpression,
  StyleFunction,
  geoJSON
} from "leaflet"
import { forwardRef, useEffect, useState } from "react"
import { GeoJSON, useMap } from "react-leaflet"

import { GeoJSONFeatureProperties } from "../../business-layer/types"

const PADDING: [number, number] = [8, 8]
const NAME_OVERRIDE: { [key: string]: string } = {
  FC: "FCT"
}
const OFFSET: { [key: string]: [number, number] } = {}
const MOBILE_OFFSET: { [key: string]: [number, number] } = {
  EN: [-5, -5],
  AN: [-5, 0],
  EB: [10, 0],
  CR: [0, 10],
  IM: [0, -5],
  AB: [0, 5],
  AK: [10, 10],
  RI: [0, -5],
  BY: [-5, 5]
}
const WEIGHT = {
  constituencyOrLGA: 1,
  state: 1
}
const DEFAULT_COLOR_PALETTE = {
  highlight: "#46AE4E",
  dimHighlight: "#AAAAAA",
  active: "#90EE90", // one-off green color from design
  inactive: ELECTION_NO_RESULT_COLOR, // one-off gray color from design
  constituencyOrLGA: "#FFFFFF",
  state: "gray"
}

/**
 * To optimize performance read
 * https://tmsvr.com/react-leaflet-map-performance-issues/
 */

export type GeoJSONMapProps = {
  data: FeatureCollection
  interactiveRegions: string[]
  palette?: Partial<typeof DEFAULT_COLOR_PALETTE>
  stateBoundaries?: FeatureCollection
  selectedFeature?: Feature<Point, GeoJSONFeatureProperties>
  showTitles?: boolean
  highlightOnHover?: "fill" | "opacity"
  avoidCollisions?: boolean
  padding?: PointExpression
  idFromProperties: (properties: GeoJSONFeatureProperties) => string
  selectRegionColor?: (
    properties: GeoJSONFeatureProperties
  ) => string | undefined
  titleFromProperties: (properties: GeoJSONFeatureProperties) => string
  onSelectFeature: (feature: Feature<Point, GeoJSONFeatureProperties>) => void
}

const MAX_BOUNDARY_ZOOM = 6.75
const TOOLTIP_COLLISION_PADDING = {
  regular: 0,
  boundary: 0
}
/**
 * solution to label conflicts inspired by the below
 * https://gis.stackexchange.com/questions/382087/how-to-remove-leaflet-tooltip-label-collision
 */
function overlap(
  rect1?: DOMRect,
  rect2?: DOMRect,
  level: "regular" | "boundary" = "boundary"
) {
  const p = TOOLTIP_COLLISION_PADDING[level]
  return (
    rect1 &&
    rect2 &&
    !(
      rect1.left > rect2.right + p ||
      rect2.left > rect1.right + p ||
      rect1.top > rect2.bottom + p ||
      rect2.top > rect1.bottom + p
    )
  )
}

export const GeoJSONMap = forwardRef<null | HTMLDivElement, GeoJSONMapProps>(
  (
    {
      data,
      interactiveRegions,
      selectedFeature,
      onSelectFeature,
      stateBoundaries,
      idFromProperties,
      selectRegionColor,
      showTitles = true,
      avoidCollisions = true,
      padding = PADDING,
      palette = {},
      highlightOnHover = "fill",
      titleFromProperties
    },
    ref
  ) => {
    const map = useMap()
    const [zoom, setZoom] = useState<number | undefined>()
    const level = zoom && zoom > MAX_BOUNDARY_ZOOM ? "regular" : "boundary"
    const mobile = window?.outerWidth < 768
    const colors = { ...DEFAULT_COLOR_PALETTE, ...palette }

    map.on("zoomend", function () {
      setZoom(map.getZoom())
    })

    if (stateBoundaries) {
      data.features = [...data.features, ...stateBoundaries.features]
    }

    useEffect(() => {
      const stateBoundaryFeature =
        selectedFeature && stateBoundaries
          ? stateBoundaries.features.find(
              (f) => f.properties?.state === selectedFeature?.properties.state
            ) || selectedFeature
          : selectedFeature

      const countryBounds = geoJSON(data).getBounds()
      const bounds = geoJSON(stateBoundaryFeature || data).getBounds()

      if (selectedFeature || !mobile) {
        map.dragging.enable()
      } else {
        map.dragging.disable()
      }

      map.invalidateSize({ debounceMoveend: true })
      map.setMaxBounds(mobile ? countryBounds : countryBounds.pad(0.5))
      try {
        map.flyToBounds(
          mobile ? bounds : bounds.pad(-0.08),
          mobile
            ? {
                duration: 0.6,
                paddingTopLeft: selectedFeature ? [0, 5] : [0, 0],
                paddingBottomRight: selectedFeature ? [0, 20] : [0, 0]
              }
            : {
                duration: 0.3,
                paddingTopLeft: selectedFeature ? [0, 0] : [0, 0],
                paddingBottomRight: selectedFeature ? [0, 300] : [0, 0]
              }
        )
      } catch (e) {
        console.log(
          "An unexpected error ocurred while moving the map. Please refresh the page",
          e
        )
      }
    }, [map, data, padding, selectedFeature])

    const tooltips: Record<
      string,
      { code: string; rect: DOMRect | undefined; show: boolean }
    > = {}

    function avoidTooltipCollision(code: string, layer: Layer) {
      const tooltip = layer.getTooltip()
      if (!tooltip) {
        return
      }

      const rect = tooltip.getElement()?.getBoundingClientRect()
      const collisions = Object.values(tooltips).filter(
        (t) => t.show && t.code !== code && overlap(t.rect, rect, level)
      )
      const show = collisions.length === 0
      tooltips[code] = { code, rect, show }
      if (!show) {
        layer.unbindTooltip()
      }
    }

    function onEachFeature(
      feature: Feature<Point, GeoJSONFeatureProperties>,
      layer: Layer
    ) {
      if (!feature || !layer) {
        return
      }

      const showTooltip =
        showTitles &&
        (!stateBoundaries ||
          (level === "regular" && feature?.properties.boundary !== true) ||
          (level === "boundary" && feature?.properties.boundary === true))

      const code = idFromProperties(feature.properties)
      if (showTooltip) {
        layer.bindTooltip(
          NAME_OVERRIDE[code] || titleFromProperties(feature.properties),
          {
            offset: (mobile ? MOBILE_OFFSET[code] : OFFSET[code]) || [0, 0],
            permanent: true,
            direction: "center",
            className:
              "stears-tooltip text-black p-0 bg-transparent border-none shadow-none text-shadow-white leading-none whitespace-pre-wrap"
          }
        )
      } else {
        layer.unbindTooltip()
      }
      if (level === "regular" && avoidCollisions) {
        avoidTooltipCollision(code, layer)
      }
    }

    const style: StyleFunction<GeoJSONFeatureProperties> = (feature) => {
      const highlight = Boolean(
        feature &&
          selectedFeature &&
          idFromProperties(feature.properties) ===
            idFromProperties(selectedFeature.properties)
      )

      const boundary = feature?.properties.boundary === true
      const interactive =
        feature &&
        !boundary &&
        interactiveRegions.includes(idFromProperties(feature.properties))

      const isState = feature?.properties.state === feature?.properties.id
      return {
        weight: isState ? WEIGHT.state : WEIGHT.constituencyOrLGA,
        opacity: 1,
        fillOpacity: 1,
        stroke: true,
        color: isState ? colors.state : colors.constituencyOrLGA,
        fill: !boundary,
        /**
         * COLOR.active (light green) - the region is interactive, but we don't know who won (upcoming, or missing "won" field in historical data)
         * COLOR.inactive (light gray) - not an interactive region
         */
        fillColor: interactive
          ? selectRegionColor?.(feature?.properties) ||
            (highlight ? colors.highlight : colors.active)
          : colors.inactive
      }
    }

    useEffect(() => {
      map.eachLayer((layer: Layer) => {
        onEachFeature((layer as any).feature, layer)
      })
    }, [zoom])

    return (
      <div
        ref={ref}
        id="map"
        className="w-full h-[325px] sm:h-[525px] relative"
      >
        <GeoJSON
          data={data}
          style={style}
          eventHandlers={{
            click: (e: LeafletMouseEvent) => {
              onSelectFeature(e.sourceTarget.feature)
            },
            mouseover: (e: LeafletMouseEvent) => {
              const interactive = interactiveRegions.includes(
                idFromProperties(e.sourceTarget.feature.properties)
              )
              if (highlightOnHover === "fill") {
                e.sourceTarget.setStyle({
                  ...style(e.sourceTarget.feature),
                  fillColor: interactive
                    ? colors.highlight
                    : colors.dimHighlight
                })
              } else {
                e.sourceTarget.setStyle({
                  ...style(e.sourceTarget.feature),
                  fillOpacity: 0.9
                })
              }
            },
            mouseout: (e: LeafletMouseEvent) => {
              e.sourceTarget.setStyle(style(e.sourceTarget.feature))
            }
          }}
        />
      </div>
    )
  }
)
